import { ButtonColor, ButtonSize, Modal, ModalType } from '@code/innovance-react-ui-kit';

import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Document, DocumentStorage, PosActivation } from 'src/front/api/apiInstance';

import {
  CardActivationType,
  ModelError,
  UploadDocumentResponse,
  UploadType,
} from 'src/front/api/generated';

import displayPDF from 'src/front/common/utils/domUtils';
import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import AsyncButton from 'src/front/components/AsyncButton';

import {
  Container,
  FormElement,
  InfoLabel,
  InfoRow,
  Label,
  LongFormRow,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import UploadDocumentForm, {
  UploadDocumentFormData,
} from 'src/front/components/UploadDocumentForm';
import { BREAK_POINTS, CARD_STATUS } from 'src/front/constant';

import { useClearDispatch } from 'src/front/contexts/OnlinePosContext';
import { Separator } from 'src/front/pages/applications/card-observation/CardObservationActivation';
import styled from 'styled-components';

import { CustomerInfo } from './OnlinePosActivation';

// TODO(ctasci): add document display for uploaded
// TODO(ctasci): replace modal type with success

export const StyledFormElement = styled(FormElement)`
  width: auto;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    width: 545px;
  }
`;

export const ButtonRow = styled.div`
  display: flex;

  & > button:not(:first-child) {
    margin-left: 20px;
  }
`;

const APPLICATION_POS_DOCUMENT_TYPE = 'MS';

interface Props {
  customerInfo?: CustomerInfo;
  goToPreviousStep: () => void;
  setIsCompleted?: (value: boolean) => void;
}

const Activation: FC<Props> = ({ customerInfo, goToPreviousStep, setIsCompleted }) => {
  const { t } = useTranslation();

  const [isRequestCompleted, setIsRequestCompleted] = useState<boolean>(false);
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [isDownloadButtonVisible, setIsDownloadButtonVisible] = useState<boolean>(false);
  const [haveDocumentUploadedWarning, setHaveDocumentUploadedWarning] = useState<boolean>(false);

  const [posResultMessage, setPosResultMessage] = useState<string>();
  const [documentId, setDocumentId] = useState<string>();
  const [uploadedDocument, setUploadedDocument] = useState<UploadDocumentResponse>();
  const [innerDocument, setInnerDocument] = useState<UploadDocumentResponse>();
  const isNewCard = customerInfo?.cardStatus === CARD_STATUS.NEW_CARD;
  const [uploadError, setUploadError] = useState<ModelError>();

  const clearSearchInputs = useClearDispatch();

  const handleApplyClick = async (): Promise<void> => {
    try {
      const { data } = await PosActivation.requestPosActivation({
        cardId: customerInfo?.cardId || '',
        cardNumber: customerInfo?.cardNumber || '',
        cardStatus: customerInfo?.cardStatus || '',
      });

      if (data.result === 'OK') {
        setPosResultMessage(data.posResultMessage);
        isNewCard
          ? setIsRequestCompleted(true)
          : setIsDownloadButtonVisible(data.isDownloadButtonVisible || false);
      }
    } catch (err) {
      // todo(ctasci): it's handled with error boundary
    }
  };

  const handleDownloadApplicationClick = async (): Promise<void> => {
    try {
      const { data } = await PosActivation.generatePosActivationDocument({
        cardId: customerInfo?.cardId || '',
      });

      if (data.result === 'OK') {
        setDocumentId(data.documentId);
        const { data: response } = await DocumentStorage.getDocument({
          documentId: data.documentId,
        });

        if (response.result === 'OK') {
          displayPDF(response.documentData);
        }
      }
    } catch (err) {
      // todo(ctasci): it's handled with error boundary
    }
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInputs();
    goToPreviousStep();
  };

  const onSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    try {
      if (innerDocument?.documentId) {
        await Document.deleteDocument({ documentId: innerDocument?.documentId });
      }
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        APPLICATION_POS_DOCUMENT_TYPE,
        UploadType.POS,
        formData.explanation,
        customerInfo?.customerId || ''
      );
      if (data.result === 'OK') {
        setInnerDocument(data);
        setUploadedDocument(data);
        setHaveDocumentUploadedWarning(true);
      }
    } catch (error) {
      if (error && isInvalidFileError(error)) {
        setUploadError(error);
      }
    }
  };

  const handleApplicationContinueClick = async (): Promise<void> => {
    try {
      if (!customerInfo || !uploadedDocument || !documentId) return;

      const { data } = await PosActivation.activateCard({
        cardId: customerInfo.cardId,
        cardNumber: customerInfo.cardNumber,
        cardStatus: customerInfo.cardStatus,
        customerId: customerInfo.customerId,
        customerNumber: customerInfo.customerNumber,
        cardActivationType: CardActivationType.POS,
        documentId: uploadedDocument.documentId,
        documentUploadData: {
          explanation: uploadedDocument.explanation,
          createDate: uploadedDocument.createDate,
          documentId: uploadedDocument.documentId,
          documentName: uploadedDocument.documentName,
          documentType: uploadedDocument.documentType,
          isOldDocument: uploadedDocument.isOldDocument,
        },
      });

      if (data.result === 'OK') {
        setIsApplicationCompleted(true);
        setIsCompleted && setIsCompleted(true);
      }
    } catch (err) {
      // todo(ctasci): it's handled with error boundary
    }
  };

  const handleApplyModalClose = (): void => {
    setPosResultMessage('');
    if (isNewCard) {
      clearSearchInputs();
      goToPreviousStep();
      setIsCompleted && setIsCompleted(false);
    }
  };

  return (
    <Container>
      {isRequestCompleted && posResultMessage ? (
        <Modal
          description={posResultMessage}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}

      <Title>
        <Trans i18nKey="posActivation_title" />
      </Title>
      {customerInfo ? (
        <>
          <InfoRow>
            <InfoLabel>
              <Trans i18nKey="general_cardNumber_label" />:
            </InfoLabel>
            {customerInfo.cardNumber}
          </InfoRow>
        </>
      ) : null}

      <Separator />
      <Label>
        <Trans i18nKey="posActivation_resetPinCode_text" />
      </Label>
      <AsyncButton
        color={ButtonColor.Primary}
        onClick={handleApplyClick}
        size={ButtonSize.Medium}
        text={t(
          isDownloadButtonVisible ? 'general_reapply_button' : 'general_components_apply_button'
        )}
        type="button"
      />

      {!isNewCard && isDownloadButtonVisible ? (
        <>
          <Separator />
          <Label>
            <Trans i18nKey="posActivation_reissuanceDocument_text" />
          </Label>
          <AsyncButton
            color={ButtonColor.Secondary}
            onClick={handleDownloadApplicationClick}
            size={ButtonSize.Medium}
            text={t(documentId ? 'general_retryDownload_button' : 'general_download_button')}
            type="button"
          />

          {documentId ? (
            <>
              <Separator />
              <UploadDocumentForm
                error={uploadError}
                handleContinue={handleApplicationContinueClick}
                setUploadedDocument={setUploadedDocument}
                submit={onSubmit}
                uploadedDocument={uploadedDocument}
              />
            </>
          ) : null}

          {posResultMessage ? (
            <Modal
              description={posResultMessage}
              onCancel={handleApplyModalClose}
              submitText={t('general_components_ok_button')}
              title={t('general_wellDone_text')}
              type={ModalType.Success}
            />
          ) : null}

          {isApplicationCompleted ? (
            <Modal
              description={t('posActivation_sendDocument_successMessage')}
              onCancel={handleApplicationSuccessClick}
              submitText={t('general_components_close_button')}
              title={t('general_wellDone_text')}
              type={ModalType.Success}
            />
          ) : null}

          {haveDocumentUploadedWarning ? (
            <LongFormRow>
              <WarningText>
                <Trans i18nKey="posActivation_afterUpload_text" />
              </WarningText>
            </LongFormRow>
          ) : null}
        </>
      ) : null}
    </Container>
  );
};

export default Activation;
